/** Pure utilities extracted from DealService. */

import type { LoanDeal } from '../models/loan.model';

/** Strips a trailing % and parses as a float. Guards against rates saved as "9%" strings. */
export function parseRate(value: unknown): number {
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  const str = String(value ?? '').trim().replace(/%$/, '');
  const n = parseFloat(str);
  return isNaN(n) ? 0 : n;
}

export function normalizeDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}-${String(parsed.getUTCDate()).padStart(2, '0')}`;
}

export function resolveBorrowerScheduleEndDate(
  loanDeal: Pick<LoanDeal, 'actualRedemptionDate' | 'expectedEndDateOverride' | 'loanEndDate'> | null | undefined
): string | null {
  const candidate = loanDeal?.actualRedemptionDate
    || loanDeal?.expectedEndDateOverride
    || loanDeal?.loanEndDate
    || null;
  return normalizeDateOnly(candidate);
}

export function isUnknownFinancialActionError(error: unknown, action: string): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const message = 'message' in error ? String((error as { message?: unknown }).message ?? '') : '';
  return message.includes(`Unknown action: ${action}`);
}

export function buildSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function stableStringify(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`);
  return `{${entries.join(',')}}`;
}

export function hashSnapshot(value: unknown): string {
  const input = stableStringify(value);
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}
